import React, { useEffect, useMemo, useState } from "react";
import styles from "./CustomTable.module.scss";
import { CUSTOM_MODULE_LOADER } from "../../constants/loader";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../Loader/Loader";
import classNames from "classnames";
import SecondaryButton from "../Button/SecondaryButton";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import QMSAccordion from "../QMSAccordion/QMSAccordion";
import { pushModal } from "../../redux/modalNavigationStore";
import { hideModal } from "../../redux/modalStore";
import { ChevronDown, ChevronUp } from "lucide-react";

const CustomTable = ({ selectedChapter, className, defaultExpanded = false }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const loadingContexts = useSelector((state) => state.loader.loadingContexts);
  const { data, permits } = useSelector((state) => state.organization);

  const riskAnalyseData = data?.risks;

  const [activeHeader, setActiveHeader] = useState("define");
  const [isTableExpanded, setIsTableExpanded] = useState(defaultExpanded);

  useEffect(() => {
    if (defaultExpanded) setIsTableExpanded(true);
  }, [defaultExpanded, selectedChapter]);

  const { orgId } = useParams();

  const isModuleLoading = loadingContexts[CUSTOM_MODULE_LOADER];
  const { t } = useTranslation();

  const toggleTable = () => {
    setIsTableExpanded(!isTableExpanded);
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "Open":
        return styles.statusOpen;
      case "In Progress":
        return styles.statusInProgress;
      case "In Review":
        return styles.statusInReview;
      case "Done":
        return styles.statusDone;
      case "Closed":
        return styles.statusClosed;
      default:
        return "";
    }
  };

  const renderDefineContent = () => {
    // Filter all permits to check if there are any questions matching the selected chapter
    const filteredPermits = Object.values(permits || {}).filter((permit) => {
      const filteredQuestions = permit.questions?.filter((question) =>
        question.chapter?.startsWith(selectedChapter),
      );
      return filteredQuestions && filteredQuestions.length > 0;
    });

    // If no filtered permits, return "Sorry, there is no data"
    if (filteredPermits.length === 0) {
      return <div className={styles.noData}>Sorry, there is no data</div>;
    }

    // Otherwise, render the filtered permits
    return (
      <div className={styles.permitWrapper}>
        {filteredPermits.map((permit) => {
          // Filter questions where chapter starts with the selectedChapter and sort by question.num
          const filteredQuestions = permit.questions
            ?.filter((question) =>
              question.chapter?.startsWith(selectedChapter),
            )
            ?.sort((a, b) => a.num - b.num); // Sort questions by num in ascending order

          return (
            <QMSAccordion
              key={permit.identifier}
              permit={{ ...permit, questions: filteredQuestions }} // Pass only filtered and sorted questions
              selectedChapter={selectedChapter}
            />
          );
        })}
      </div>
    );
  };

  const renderMeasureContent = () => {
    if (!selectedChapter) {
      return <div className={styles.noData}>Sorry, there is no data</div>;
    }

    // Filter the audits based on whether the qms_id starts with the selectedChapter
    const filteredAudits = Object.values(data?.audit_tasks || {}).filter((audit) =>
      audit.form.some((formItem) =>
        formItem.risk.qms_id.startsWith(selectedChapter),
      ),
    );

    // Filter the findings based on whether the qms_id starts with the selectedChapter
    const filteredFindings = Object.values(data?.findings || {}).filter((finding) =>
      finding?.risk?.qms_id?.startsWith(selectedChapter),
    );

    if (filteredAudits.length === 0 && filteredFindings.length === 0) {
      return <div className={styles.noData}>{t("customTable.noData")}</div>;
    }

    return (
      <div className={styles.sectionGrid}>
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>
              {t("customTable.sections.findings")}
            </div>
            {filteredFindings.length > 0 && (
              <SecondaryButton
                text={t("customTable.buttons.viewAllFindings")}
                className={styles.sectionAction}
                onClick={() => navigate(`/${orgId}/findings`)}
              />
            )}
          </div>
          <div className={styles.sectionBody}>
            {filteredFindings.map((finding) => (
              <div key={finding.key} className={styles.itemRow}>
                <a
                  href={`/${orgId}/findings/${finding.key}`}
                  onClick={(e) => {
                    e.preventDefault(); // prevent normal link navigation
                    handleLinkedModalOpen(`findings/${finding.key}`);
                  }}
                  className={classNames(styles.key, getStatusClass(finding.status))}
                >
                  {finding.key}
                </a>
                <span className={styles.itemText}>{finding.description}</span>
              </div>
            ))}
            {filteredFindings.length === 0 && (
              <div className={styles.inlineEmpty}>{t("customTable.noData")}</div>
            )}
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>
              {t("customTable.sections.audits")}
            </div>
            {filteredAudits.length > 0 && (
              <SecondaryButton
                text={t("customTable.buttons.viewAllAudits")}
                className={styles.sectionAction}
                onClick={() => navigate(`/${orgId}/audits`)}
              />
            )}
          </div>
          <div className={styles.sectionBody}>
            {filteredAudits.map((audit) => (
              <div key={audit.key} className={styles.itemRow}>
                <a
                  href={`/${orgId}/audits/${audit.key}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleLinkedModalOpen(`audits/${audit.key}`);
                  }}
                  className={classNames(styles.key, getStatusClass(audit.status))}
                >
                  {audit.key}
                </a>
                <span className={styles.itemText}>{audit.name}</span>
              </div>
            ))}
            {filteredAudits.length === 0 && (
              <div className={styles.inlineEmpty}>{t("customTable.noData")}</div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderAnalyseContent = () => {
    if (!selectedChapter) {
      return <div className={styles.noData}>{t("customTable.noData")}</div>;
    }

    // Convert riskAnalyseData to an array to filter it
    const riskEntries = Object.values(riskAnalyseData || {});

    // Filter risks based on selectedChapter
    const filteredRisks = riskEntries.filter((risk) =>
      risk.qms_id.startsWith(selectedChapter),
    );

    // Sort the filtered risks by qms_id using natural sorting
    const sortedRisks = filteredRisks.sort((a, b) => {
      const regex = /(\D+)(\d+)(.*)/;
      const [, aPrefix, aNumber] = a.qms_id.match(regex);
      const [, bPrefix, bNumber] = b.qms_id.match(regex);

      if (aPrefix === bPrefix) {
        return parseInt(aNumber, 10) - parseInt(bNumber, 10);
      }

      return a.qms_id.localeCompare(b.qms_id, undefined, { numeric: true });
    });

    // Filter by risk level
    const filteredHighRisks = sortedRisks.filter(
      (risk) => risk.level.toLowerCase() === "high",
    );
    const filteredMediumRisks = sortedRisks.filter(
      (risk) => risk.level.toLowerCase() === "medium",
    );
    const filteredLowRisks = sortedRisks.filter(
      (risk) => risk.level.toLowerCase() === "low",
    );

    if (
      filteredHighRisks.length === 0 &&
      filteredMediumRisks.length === 0 &&
      filteredLowRisks.length === 0
    ) {
      return <div className={styles.noData}>{t("customTable.noData")}</div>;
    }

    return (
      <div className={styles.sectionStack}>
        {filteredHighRisks.length > 0 && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={classNames(styles.sectionTitle, styles.highTitle)}>
                {t("customTable.analyse.high")}
              </div>
            </div>
            <div className={styles.sectionBody}>
              {filteredHighRisks.map((risk) => (
                <div key={risk.risk_id} className={styles.itemRow}>
                  <div className={classNames(styles.badge, styles.highBadge)}>
                    {String(risk.risk_id)}
                  </div>
                  <span className={styles.itemText}>{risk.description}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {filteredMediumRisks.length > 0 && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={classNames(styles.sectionTitle, styles.mediumTitle)}>
                {t("customTable.analyse.medium")}
              </div>
            </div>
            <div className={styles.sectionBody}>
              {filteredMediumRisks.map((risk) => (
                <div key={risk.risk_id} className={styles.itemRow}>
                  <div className={classNames(styles.badge, styles.mediumBadge)}>
                    {String(risk.risk_id)}
                  </div>
                  <span className={styles.itemText}>{risk.description}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {filteredLowRisks.length > 0 && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={classNames(styles.sectionTitle, styles.lowTitle)}>
                {t("customTable.analyse.low")}
              </div>
            </div>
            <div className={styles.sectionBody}>
              {filteredLowRisks.map((risk) => (
                <div key={risk.risk_id} className={styles.itemRow}>
                  <div className={classNames(styles.badge, styles.lowBadge)}>
                    {String(risk.risk_id)}
                  </div>
                  <span className={styles.itemText}>{risk.description}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderImproveContent = () => {
    if (!selectedChapter) {
      return <div className={styles.noData}>Sorry, there is no data</div>;
    }

    // Convert data.findings to an array
    const findingsArray = Object.values(data?.findings || {});

    // Filter improvements based on whether their linked_findings have a qms_id that starts with the selectedChapter
    const filteredImprovements = Object.values(data?.improvements || {}).filter(
      (improvement) =>
        Array.isArray(improvement.linked_findings) &&
        improvement.linked_findings.some((findingKey) => {
          const linkedFinding = findingsArray.find((f) => f.key === findingKey);
          return linkedFinding?.risk?.qms_id?.startsWith(selectedChapter);
        }),
    );

    if (filteredImprovements.length === 0) {
      return <div className={styles.noData}>{t("customTable.noData")}</div>;
    }

    return (
      <div className={styles.sectionStack}>
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>
              {t("customTable.sections.improvements")}
            </div>
            <SecondaryButton
              text={t("customTable.buttons.viewAllImprovements")}
              className={styles.sectionAction}
              onClick={() => navigate(`/${orgId}/improvement-projects`)}
            />
          </div>
          <div className={styles.sectionBody}>
            {filteredImprovements.map((improvement) => (
              <div key={improvement.key} className={styles.itemRow}>
                <a
                  href={`/${orgId}/improvement-projects/${improvement.key}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleLinkedModalOpen(
                      `improvement-projects/${improvement.key}`,
                    );
                  }}
                  className={classNames(
                    styles.key,
                    getStatusClass(improvement.status),
                  )}
                >
                  {improvement.key}
                </a>
                <span className={styles.itemText}>{improvement.description}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeHeader) {
      case "define":
        return renderDefineContent();
      case "measure":
        return renderMeasureContent();
      case "analyse":
        return renderAnalyseContent();
      case "improve":
        return renderImproveContent();
      default:
        return null;
    }
  };

  // Disable tab switching if the table is collapsed
  const handleTabClick = (tab) => {
    if (isTableExpanded) {
      setActiveHeader(tab);
    }
  };

  const tabs = useMemo(
    () => [
      { key: "define", label: t("treeLayout.columns.define") },
      { key: "measure", label: t("treeLayout.columns.measure") },
      { key: "analyse", label: t("treeLayout.columns.analyse") },
      { key: "improve", label: t("treeLayout.columns.improve") },
    ],
    [t],
  );

  const handleLinkedModalOpen = (path) => {
    dispatch(pushModal(window.location.pathname));
    dispatch(hideModal());
    navigate(`/${orgId}/${path}`);
  };

  return (
    selectedChapter && (
      <div
        className={classNames(
          styles.wrapper,
          className,
          isTableExpanded && styles.expanded,
        )}
      >
        <div className={styles.toolbar}>
          <SecondaryButton
            className={styles.toggleButton}
            onClick={toggleTable}
            text={
              isTableExpanded
                ? t("customTable.toggle.collapse")
                : t("customTable.toggle.expand")
            }
            icon={
              isTableExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />
            }
          />
        </div>
        <div className={styles.tabSwitcher} role="tablist">
          {tabs.map((tab) => {
            const isActive = activeHeader === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => handleTabClick(tab.key)}
                className={classNames(
                  styles.tabButton,
                  isActive && styles.tabButtonActive,
                )}
                disabled={!isTableExpanded}
                role="tab"
                aria-selected={isActive}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
        {isModuleLoading ? (
          <Loader text={t("customTable.loading")} />
        ) : (
          isTableExpanded && (
            <div className={styles.panel} role="tabpanel">
              {renderContent()}
            </div>
          )
        )}
      </div>
    )
  );
};

export default CustomTable;
